import { Spacing } from '@persona-id/design-tokens';
import { intersection } from 'lodash/fp';
import { useMemo } from 'react';
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import {
  CaseAssignmentPolicyAttributes,
  DISTRIBUTION_TYPE_LABELS,
} from 'dashboard/models/case-assignment-policy';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { Team } from 'dashboard/models/team';
import { User, UserAvailability } from 'dashboard/models/user';
import SourceCard from 'dashboard/views/DashboardCaseAssignmentPolicyShowView/SourceCard';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';

const DISTRIBUTION_TYPE_OPTIONS: {
  label: string;
  value: CaseAssignmentPolicyAttributes['distribution-type'];
}[] = [
  {
    label: DISTRIBUTION_TYPE_LABELS['round_robin'],
    value: 'round_robin',
  },
  {
    label: DISTRIBUTION_TYPE_LABELS['case_creator'],
    value: 'case_creator',
  },
  {
    label: DISTRIBUTION_TYPE_LABELS['inquiry_creator'],
    value: 'inquiry_creator',
  },
  {
    label: DISTRIBUTION_TYPE_LABELS['fewest_cases'],
    value: 'fewest_cases',
  },
];

const ORDER_BY_OPTIONS: {
  label: string;
  value: CaseAssignmentPolicyAttributes['assignment-order-by'];
}[] = [
  {
    label: 'Created at',
    value: 'created_at',
  },
  {
    label: 'SLA expiration',
    value: 'sla_expires_at',
  },
];

const AVAILABILITY_OPTIONS: {
  label: string;
  value: UserAvailability;
}[] = [
  {
    label: 'Available',
    value: UserAvailability.Available,
  },
  {
    label: 'Unavailable',
    value: UserAvailability.Unavailable,
  },
  {
    label: 'Busy',
    value: UserAvailability.Busy,
  },
];

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${Spacing[4]};
  grid-row-gap: ${Spacing[4]};
`;

interface FormEditCaseAssignmentPolicyProps {
  users: User[];
  teams: Team[];
  caseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  caseQueues: CaseQueue[];
  updateCaseAssignmentPolicy: (formData: EditCaseAssignmentPolicyFormData) => void;
}

const FormEditCaseAssignmentPolicy = ({
  users,
  teams,
  caseTemplateBasicsOptions,
  caseQueues,
  updateCaseAssignmentPolicy,
}: FormEditCaseAssignmentPolicyProps) => {
  const {
    register,
    control,
    formState: { errors, isDirty },
    handleSubmit,
    getValues,
    setValue,
    trigger,
  } = useFormContext<EditCaseAssignmentPolicyFormData>();

  const onSubmit: SubmitHandler<EditCaseAssignmentPolicyFormData> = (data) =>
    updateCaseAssignmentPolicy(data);

  const userOptions = useMemo(
    () =>
      users.map((user) => {
        return {
          label: user.attributes['email-address'],
          value: user.id,
        };
      }),
    [users],
  );

  const teamOptions = useMemo(
    () =>
      teams.map((team) => {
        return {
          label: team.attributes['name'],
          value: team.id,
        };
      }),
    [teams],
  );

  const assigneeOptions = [
    {
      label: 'Teams',
      value: 'teams',
      options: teamOptions,
    },
    {
      label: 'Users',
      value: 'users',
      options: userOptions,
    },
  ];

  return (
    <>
      <NavConfirmPrompt prevent={isDirty} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <Card.Body>
            <Card.Title>Assignment Policy</Card.Title>
            <FormContainer>
              <div>
                <Label size='md' htmlFor='form-edit-case-assignment-policy__name' weight='bold'>
                  Policy name
                </Label>
                <Spacer height={1} />
                <Input
                  {...register('name', {
                    required: 'Please give your assignment policy a name',
                    minLength: {
                      value: 2,
                      message: 'Assignment policy names must be at least 2 letters long',
                    },
                  })}
                  id='form-edit-case-assignment-policy__name'
                  placeholder='Name'
                  type='text'
                />
                {errors.name && <FormError>{errors.name.message}</FormError>}
              </div>

              <div>
                <Label
                  htmlFor='form-edit-case-assignment-policy__order-by'
                  id='form-edit-case-assignment-policy__order-by--label'
                  size='md'
                  weight='bold'
                >
                  Case sort order
                </Label>
                <Spacer height={1} />
                <Controller
                  control={control}
                  name='assignmentOrderBy'
                  rules={{
                    required: 'Please select an assignment order',
                  }}
                  render={({ field: { onChange, value } }) => (
                    <ReactSelect
                      id='form-edit-case-assignment-policy__order-by-type'
                      aria-labelledby='form-edit-case-assignment-policy__order-by--label'
                      options={ORDER_BY_OPTIONS}
                      value={value}
                      onChange={onChange}
                      isSearchable
                      portal
                    />
                  )}
                />
                {errors.assignmentOrderBy && (
                  <FormError>{errors.assignmentOrderBy.message}</FormError>
                )}
              </div>

              <div>
                <Label
                  htmlFor='form-edit-case-assignment-policy__distribution'
                  id='form-edit-case-assignment-policy__distribution--label'
                  size='md'
                  weight='bold'
                >
                  Distribution
                </Label>
                <Spacer height={1} />
                <Controller
                  control={control}
                  name='distributionType'
                  rules={{
                    required: 'Please select a distribution type',
                  }}
                  render={({ field: { onChange, value } }) => (
                    <ReactSelect
                      id='form-edit-case-assignment-policy__distribution-type'
                      aria-labelledby='form-edit-case-assignment-policy__distribution--label'
                      options={DISTRIBUTION_TYPE_OPTIONS}
                      value={value}
                      onChange={onChange}
                      isSearchable
                      portal
                    />
                  )}
                />
                {errors.distributionType && (
                  <FormError>{errors.distributionType.message}</FormError>
                )}
              </div>

              <div>
                <Label
                  htmlFor='form-edit-case-assignment-policy__agents'
                  id='form-edit-case-assignment-policy__agents--label'
                  size='md'
                  weight='bold'
                >
                  Agents
                </Label>
                <Spacer height={1} />
                <Controller
                  control={control}
                  name='userIds'
                  render={({ field: { onChange, value } }) => (
                    <ReactMultiSelect
                      portal
                      isMulti
                      aria-labelledby='form-edit-case-assignment-policy__agents--label'
                      options={assigneeOptions}
                      searchEnabled
                      selectAllEnabled
                      onChange={(assigneeIds: string[]) => {
                        const teamIds = assigneeIds.filter((assigneeId) =>
                          assigneeId.startsWith('team_'),
                        );
                        const userIds = assigneeIds.filter((assigneeId) =>
                          assigneeId.startsWith('user_'),
                        );
                        setValue('teamIds', teamIds, { shouldDirty: true });
                        onChange(userIds);
                      }}
                      value={getValues('teamIds').concat(value)}
                      placeholderGenerator={generatePlaceHolderDisplay({
                        label: { single: 'agent', multiple: 'agents' },
                        emptyIsAll: true,
                      })}
                    />
                  )}
                />
                {errors['userIds'] && errors['userIds'].type === 'requireAssignees' && (
                  <FormError>{errors['userIds'].message}</FormError>
                )}
              </div>

              <div>
                <Label
                  htmlFor='form-edit-case-assignment-policy__allowed-user-availabilities'
                  id='form-edit-case-assignment-policy__allowed-user-availabilities--label'
                  size='md'
                  weight='bold'
                >
                  Assign when agent is...
                </Label>
                <Spacer height={1} />
                <Controller
                  control={control}
                  name='allowedUserAvailabilities'
                  render={({ field: { onChange, value } }) => (
                    <ReactMultiSelect
                      id='form-edit-case-assignment-policy__allowed-user-availabilities'
                      aria-labelledby='form-edit-case-assignment-policy__allowed-user-availabilities--label'
                      options={AVAILABILITY_OPTIONS}
                      value={value}
                      onChange={(values: string[]) => {
                        onChange(values);
                        trigger('unassignUserAvailabilities');
                      }}
                      placeholder={
                        value.length === 0
                          ? 'All User Availabilities'
                          : AVAILABILITY_OPTIONS.filter((option) => value.includes(option.value))
                              .map((option) => option.label)
                              .join(', ')
                      }
                      selectAllEnabled
                      searchEnabled
                      portal
                    />
                  )}
                />
                {errors.allowedUserAvailabilities && (
                  <FormError>{errors.allowedUserAvailabilities.message}</FormError>
                )}
              </div>

              <div>
                <Label
                  htmlFor='form-edit-case-assignment-policy__unassign-user-availabilities'
                  id='form-edit-case-assignment-policy__unassign-user-availabilities--label'
                  size='md'
                  weight='bold'
                >
                  (Optional) Unassign when agent is...
                </Label>
                <Spacer height={1} />
                <Controller
                  control={control}
                  name='unassignUserAvailabilities'
                  rules={{
                    validate: (value) =>
                      (getValues('allowedUserAvailabilities').length === 0 && value.length === 0) ||
                      (getValues('allowedUserAvailabilities').length > 0 &&
                        intersection(getValues('allowedUserAvailabilities'), value).length === 0) ||
                      'Cannot overlap with assignable user availabilities',
                  }}
                  render={({ field: { onChange, value } }) => (
                    <ReactMultiSelect
                      id='form-edit-case-assignment-policy__unassign-user-availabilities'
                      aria-labelledby='form-edit-case-assignment-policy__unassign-user-availabilities--label'
                      aria-describedby='verification-templates-selector-label'
                      options={AVAILABILITY_OPTIONS}
                      value={value}
                      onChange={onChange}
                      placeholder={
                        value.length === 0
                          ? 'No User Availabilities'
                          : AVAILABILITY_OPTIONS.filter((option) => value.includes(option.value))
                              .map((option) => option.label)
                              .join(', ')
                      }
                      selectAllEnabled
                      searchEnabled
                      portal
                    />
                  )}
                />
                {errors.unassignUserAvailabilities && (
                  <FormError>{errors.unassignUserAvailabilities.message}</FormError>
                )}
              </div>

              <div>
                <Label
                  size='md'
                  htmlFor='form-edit-case-assignment-policy__max-capacity'
                  weight='bold'
                >
                  Agent maximum capacity (optional)
                </Label>
                <Spacer height={1} />
                <Input
                  {...register('maxCapacity', {
                    min: { value: 1, message: 'Max capacity must be positive' },
                  })}
                  id='form-edit-case-assignment-policy__max-capacity'
                  min={1}
                  placeholder='None'
                  type='number'
                />
                {errors.maxCapacity && <FormError>{errors.maxCapacity.message}</FormError>}
              </div>
            </FormContainer>
          </Card.Body>
        </Card>
        <SourceCard caseTemplateBasicsOptions={caseTemplateBasicsOptions} caseQueues={caseQueues} />
      </form>
    </>
  );
};

export interface EditCaseAssignmentPolicyFormData {
  name: string;
  assignmentOrderBy: CaseAssignmentPolicyAttributes['assignment-order-by'];
  distributionType: CaseAssignmentPolicyAttributes['distribution-type'];
  maxCapacity: number | null;
  allowedUserAvailabilities: UserAvailability[];
  unassignUserAvailabilities: UserAvailability[];
  userIds: string[];
  teamIds: string[];
  caseSourceType: CaseAssignmentPolicyAttributes['case-source-list']['source-type'];
  caseSourceIds: string[];
}

export default FormEditCaseAssignmentPolicy;
